import React, { useContext } from 'react';
import EDSwitch from '../../../../../../BaseComponents/Switch/EDSwitch';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { styled } from '@mui/system';
import { Theme } from '@mui/material/styles';
import EDDatePicker from '../../../../../../BaseComponents/DatePicker/EDDatePicker';
import { themeColors } from '../../../../../../BaseComponents/ThemeProvider/AvenyThemeColors';
import { ITypeTreeConditionObject } from '../../../../../../Types/UrvalTypes';
import TypeTreePickerDialogButton from '../../../../../../Components/Urval/Components/Condition/Components/TypeTreePicker/TypeTreePickerDialogButton';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import {
  findCollection,
  UNKNOWN_ATTRIBUTE,
} from '../../../../../../Model/avenyModelUtils';
import EDChip from '../../../../../../BaseComponents/Chip/EDChip';
import { ResursValjare } from './ResursValjare';
import { ResursTypContextProvider } from '../../../Contexts/ResursTypContext/ResursTypContext';
import { IBokningsrubrik } from '../../../ObjektVyer/Aktivitet/AktivitetResursValjare';

const AKTIVITET_COLLECTION = 'BOAktivitet';
const AKTIVITET_TYP_ATTRIBUTE = '_typ';
const TYPE_CONDITION_ID = 'planering-toolbar-type';

const CONDITION_TO_NAMN: Record<string, string> = {
  AKTOVRIGT: 'Övrigt',
  GUDSTJANST: 'Gudstjänst',
  DOP: 'Dop',
  BEGRAVNING: 'Begravning',
  VIGSEL: 'Vigsel',
  KONFIRMATION: 'Konfirmation',
  GRAVSATTNING: 'Gravsättning',
  LOKALUTHYRNING: 'Lokaluthyrning',
  SNABBOKNING: 'Snabbokning',
};

interface IProps {
  storedPlaneringSetting: boolean;
  setStoredPlaneringSetting: (value: boolean) => void;
  sumOfPlaneradeAkt: number;
  sumOfAktiviteter: number;
  setStoredBokadeAktiviteterSetting: (value: boolean) => void;
  bokadeAktiviteterSetting: boolean;
  datumFr: string;
  setDatumFr: (val: string) => void;
  isValidDateFr: boolean;
  setIsValidDateFr: (val: boolean) => void;
  datumTi: string;
  setDatumTi: (val: string) => void;
  isValidDateTiSetting: boolean;
  setStoredIsValidDateTiSetting: (val: boolean) => void;
  typeConditionObject: ITypeTreeConditionObject | undefined;
  setTypeConditionObject: (value: ITypeTreeConditionObject) => void;
  bokningsrubriker: IBokningsrubrik[];
  setBokningsrubriker: (value: IBokningsrubrik[]) => void;
}

export type ResursBastyp = 'Plats' | 'Person' | 'Övrigt';

const ALLOWED_BASE_TYPES: ResursBastyp[] = ['Plats', 'Person'];

const PlaneringToolbar: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { avenyCollectionMap } = useContext(ModelContext);

  const collection = findCollection(avenyCollectionMap, AKTIVITET_COLLECTION);

  const resolvedTypeConditionObject: ITypeTreeConditionObject | undefined =
    props.typeConditionObject ??
    (collection
      ? {
          type: 'TypeTree',
          id: TYPE_CONDITION_ID,
          attribute:
            collection.Attributes[AKTIVITET_TYP_ATTRIBUTE] ?? UNKNOWN_ATTRIBUTE,
          condition: [],
          implicitCondition: [],
          typeTree: collection.RootType,
        }
      : undefined);

  return (
    <EDBox
      sx={theme => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        borderBottom: `1px solid ${themeColors.customLight.silver}`,
        paddingTop: 2.5,
        paddingRight: 2.5,
        paddingBottom: 2,
        paddingLeft: 2.5,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <EDBox
        sx={{
          marginBottom: 1,
        }}
      >
        <HeaderTypography>Inställningar</HeaderTypography>
      </EDBox>

      <EDBox
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <EDBox sx={{ display: 'flex', flexDirection: 'column' }}>
          <EDBox
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0,
            }}
          >
            <EDTypography>
              Planerade aktiviteter ({props.sumOfPlaneradeAkt})
            </EDTypography>
            <EDSwitch
              title={
                props.storedPlaneringSetting
                  ? 'Dölj planering'
                  : 'Visa planering'
              }
              checked={props.storedPlaneringSetting}
              onChange={(_, checked) => {
                props.setStoredPlaneringSetting(checked);
              }}
              testId="planering-switch"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: themeColors.customPlaneringColors.planeradAktivitet,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor:
                    themeColors.customPlaneringColors.planeradAktivitet,
                  opacity: 1,
                },
              }}
            />
            <EDTypography>
              Bokade aktiviteter ({props.sumOfAktiviteter})
            </EDTypography>
            <EDSwitch
              title={
                props.bokadeAktiviteterSetting
                  ? 'Dölj aktiviteter'
                  : 'Visa aktiviteter'
              }
              checked={props.bokadeAktiviteterSetting}
              onChange={(_, checked) =>
                props.setStoredBokadeAktiviteterSetting(checked)
              }
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: themeColors.customPlaneringColors.bokadAktivitet,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor:
                    themeColors.customPlaneringColors.bokadAktivitet,
                  opacity: 1,
                },
              }}
            />
          </EDBox>

          <EDBox sx={{ marginTop: '16px' }}>
            <EDBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <EDTypography variant="caption">
                Välj datumintervall:
              </EDTypography>
              <EDBox
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                  mt: 0,
                  mb: 2,
                }}
              >
                <EDBox>
                  <EDDatePicker
                    label="Från datum"
                    tooltip="Inkludera aktiviteter med startdatum från och med detta datum"
                    value={props.datumFr}
                    onDateChange={(newDate, isValid) => {
                      props.setDatumFr(newDate);
                      props.setIsValidDateFr(isValid);
                    }}
                    required
                    allowOnlyNumbers
                  />
                  <EDTypography
                    variant="caption"
                    color="error"
                    fontSize={'0.75rem'}
                  >
                    {props.datumFr && !props.isValidDateFr
                      ? 'Valt datum är ogiltigt'
                      : ''}
                  </EDTypography>
                </EDBox>
                <EDBox>
                  <EDDatePicker
                    label="Till datum"
                    tooltip="Inkludera aktiviteter med startdatum till och med detta datum"
                    value={props.datumTi}
                    onDateChange={(newDate, isValid) => {
                      props.setDatumTi(newDate);
                      props.setStoredIsValidDateTiSetting(isValid);
                    }}
                    required
                    allowOnlyNumbers
                  />
                  <EDTypography
                    variant="caption"
                    color="error"
                    fontSize={'0.75rem'}
                  >
                    {props.datumTi && !props.isValidDateTiSetting
                      ? 'Valt datum är ogiltigt'
                      : ''}
                  </EDTypography>
                </EDBox>
              </EDBox>
            </EDBox>
          </EDBox>
        </EDBox>

        <EDBox sx={{ marginLeft: '40px' }}>
          <ResursTypContextProvider>
            <ResursValjare
              bokningsrubriker={props.bokningsrubriker}
              setBokningsrubriker={props.setBokningsrubriker}
              allowedBaseTypes={ALLOWED_BASE_TYPES}
            />
          </ResursTypContextProvider>
        </EDBox>
        {resolvedTypeConditionObject && (
          <EDBox sx={{ pl: 2, marginLeft: 2.5 }}>
            <EDTypography variant="caption">Välj typ:</EDTypography>
            <EDBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {resolvedTypeConditionObject.condition.map(key => (
                <EDBox
                  key={key}
                  sx={(theme: Theme) => {
                    const color =
                      (
                        theme.customProperties
                          ?.AktivitetColors as unknown as Record<string, string>
                      )?.[key.toLowerCase()] ?? undefined;
                    return {
                      '& .MuiChip-root': {
                        backgroundColor: color,
                        borderColor: color,
                        color: 'white',
                      },
                      '& .MuiChip-deleteIcon': {
                        color: 'whitesmoke',
                        '&:hover': { color: 'white' },
                      },
                    };
                  }}
                >
                  <EDChip
                    label={CONDITION_TO_NAMN[key] ?? key}
                    onDelete={() =>
                      props.setTypeConditionObject({
                        ...resolvedTypeConditionObject,
                        condition: resolvedTypeConditionObject.condition.filter(
                          c => c !== key
                        ),
                      })
                    }
                    variant="outlined"
                    marginRight={0}
                  />
                </EDBox>
              ))}
              <TypeTreePickerDialogButton
                key={resolvedTypeConditionObject.condition.join(',')}
                conditionObject={resolvedTypeConditionObject}
                setConditionObject={props.setTypeConditionObject}
              />
            </EDBox>
          </EDBox>
        )}
      </EDBox>
    </EDBox>
  );
};

const HeaderTypography = styled(EDTypography, { name: 'HeaderTypography' })({
  fontSize: 20,
  fontWeight: 'bold',
});

export default PlaneringToolbar;
